import { useEffect, useRef, useState } from "react";
import { X, Trash2, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { IconTile } from "@/components/icons/LunaIcon";
import { serverDetail, isTermsRequired, DETAIL_STATUSES } from "@/lib/errorText";
import { makeT } from "@/lib/i18n";
import { dataUrlMime, downloadName, mergeById } from "@/lib/files";
import { newGeneration, runGeneration, useRunningGenerations } from "@/lib/generations";
import {
  GENERATE_ROUTES, fetchGeneratedFilesPage,
  deleteGeneratedPdf, deleteGeneratedExcel, deleteGeneratedWord, deleteGeneratedPpt, deleteGeneratedTableImage, deleteGeneratedChart,
} from "@/lib/api";

// m:ss — 0:07, 1:42 (the generation's elapsed time).
function elapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// One panel, four flavors — "Ürettiklerim: PDF / Excel / Word / PowerPoint"
// are the exact same form + list layout, only the generate/fetch/delete
// calls, button gradient and a couple of labels differ per kind. The icon is
// Luna's own Celestial one, keyed by `kind` itself (pdf, excel, word, ppt,
// table, chart), so it matches the sidebar row that opened the panel. The
// request goes to GENERATE_ROUTES[kind]; making: what it makes (FriendPanel's
// words for it — lib/generations.js keeps it with the generation).
const KIND_CONFIG = {
  pdf: {
    gradient: "linear-gradient(90deg,#f43f5e,#c084fc)", ext: "pdf",
    title: { tr: "Ürettiklerim: PDF", en: "My Generated PDFs" },
    generateLabel: { tr: "PDF Oluştur", en: "Generate PDF" },
    placeholder: {
      tr: "Verini buraya yapıştır — bir liste, tablo, satır satır bilgi, ne olursa olsun. Luna onu düzenli bir tabloya çevirecek.",
      en: "Paste your data here — a list, a table, line-by-line info, anything. Luna will turn it into a clean table.",
    },
    making: "pdf", deleteFile: deleteGeneratedPdf,
  },
  excel: {
    gradient: "linear-gradient(90deg,#10b981,#6366f1)", ext: "xlsx",
    title: { tr: "Ürettiklerim: Excel", en: "My Generated Excel Files" },
    generateLabel: { tr: "Excel Oluştur", en: "Generate Excel" },
    placeholder: {
      tr: "Verini buraya yapıştır — bir liste, tablo, satır satır bilgi, ne olursa olsun. Luna onu düzenli bir tabloya çevirecek.",
      en: "Paste your data here — a list, a table, line-by-line info, anything. Luna will turn it into a clean table.",
    },
    making: "xlsx", deleteFile: deleteGeneratedExcel,
  },
  word: {
    gradient: "linear-gradient(90deg,#0ea5e9,#6366f1)", ext: "docx",
    title: { tr: "Ürettiklerim: Word", en: "My Generated Word Docs" },
    generateLabel: { tr: "Word Belgesi Oluştur", en: "Generate Word Doc" },
    placeholder: {
      tr: "İçeriğini buraya yapıştır — notlar, taslak bir metin, ne olursa olsun. Luna onu başlıklara bölünmüş düzenli bir Word belgesine çevirecek.",
      en: "Paste your content here — notes, a rough draft, anything. Luna will turn it into a clean, sectioned Word document.",
    },
    making: "docx", deleteFile: deleteGeneratedWord,
  },
  ppt: {
    gradient: "linear-gradient(90deg,#f59e0b,#ef4444)", ext: "pptx",
    title: { tr: "Ürettiklerim: PowerPoint", en: "My Generated Slides" },
    generateLabel: { tr: "Sunum Oluştur", en: "Generate Slides" },
    placeholder: {
      tr: "İçeriğini buraya yapıştır — notlar, konu başlıkları, ne olursa olsun. Luna onu maddeler halinde bir sunuma çevirecek.",
      en: "Paste your content here — notes, talking points, anything. Luna will turn it into a bulleted slide deck.",
    },
    making: "pptx", deleteFile: deleteGeneratedPpt,
  },
  table: {
    gradient: "linear-gradient(90deg,#8b5cf6,#6366f1)", ext: "png",
    title: { tr: "Ürettiklerim: Tablo", en: "My Generated Tables" },
    generateLabel: { tr: "Tablo Oluştur", en: "Generate Table" },
    placeholder: {
      tr: "Verini buraya yapıştır — bir liste, tablo, satır satır bilgi, ne olursa olsun. Luna onu görsel olarak paylaşılabilir düzenli bir tablo resmine çevirecek.",
      en: "Paste your data here — a list, a table, line-by-line info, anything. Luna will turn it into a clean, shareable table image.",
    },
    making: "tableimg", deleteFile: deleteGeneratedTableImage,
  },
  chart: {
    gradient: "linear-gradient(90deg,#f97316,#eab308)", ext: "png",
    title: { tr: "Ürettiklerim: Grafik", en: "My Generated Charts" },
    generateLabel: { tr: "Grafik Oluştur", en: "Generate Chart" },
    placeholder: {
      tr: "Sayısal verini buraya yapıştır — kategori/değer çiftleri, bir liste, ne olursa olsun. Luna en uygun grafiği (çubuk, çizgi ya da pasta) çizecek.",
      en: "Paste your numeric data here — category/value pairs, a list, anything. Luna will draw the chart type (bar, line or pie) that fits best.",
    },
    making: "chart", deleteFile: deleteGeneratedChart,
  },
};

// The same files can simply be asked for in chat, like with ChatGPT (backend
// services/chat_actions.py, every plan): an example per kind, shown above
// the form.
const CHAT_TIPS = {
  pdf: { tr: "“Yapay zekanın tarihi hakkında bir PDF rapor hazırla”", en: "“Make a PDF report on the history of AI”" },
  word: { tr: "“Okul projem için bir Word belgesi hazırla”", en: "“Write a Word document for my school project”" },
  excel: { tr: "“Aylık bütçem için bir Excel tablosu hazırla”", en: "“Create an Excel sheet for my monthly budget”" },
  ppt: { tr: "“Yenilenebilir enerji hakkında bir sunum hazırla”", en: "“Prepare a presentation about renewable energy”" },
};

export default function DocGeneratorPanel({ kind, lang, mode, onClose }) {
  const t = makeT(lang);
  const cfg = KIND_CONFIG[kind];
  const { gradient, ext: fileExt } = cfg;
  const panelTitle = t(cfg.title.tr, cfg.title.en);
  const generateLabel = t(cfg.generateLabel.tr, cfg.generateLabel.en);
  const dataPlaceholder = t(cfg.placeholder.tr, cfg.placeholder.en);
  const chatTip = CHAT_TIPS[kind];

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [rawData, setRawData] = useState("");
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // The server pages the list the way it pages the picture gallery
  // (lib/api.js fetchGeneratedFilesPage): the older files come with "Daha
  // fazla göster" — only the first page used to be read (audit client-10).
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const { deleteFile } = cfg;
  const route = GENERATE_ROUTES[kind];
  // A file of this kind being made (lib/generations.js): this panel's own
  // request, or one picked up again after a reload (pages/Luna.jsx) — the
  // form says so, with how long it has taken, and asks for no second one
  // meanwhile; the list is read again once it is done.
  const running = useRunningGenerations().find((w) => w.route === route) || null;
  const busy = generating || Boolean(running);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  useEffect(() => {
    fetchGeneratedFilesPage(kind)
      .then((page) => {
        setFiles(page.files);
        setNextBefore(page.hasMore ? page.nextBefore : null);
      })
      .catch(() => toast.error(t("Dosyalar yüklenemedi", "Couldn't load files")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchGeneratedFilesPage(kind, { before: nextBefore });
      setFiles((have) => mergeById(have, page.files));
      setNextBefore(page.hasMore ? page.nextBefore : null);
    } catch {
      toast.error(t("Dosyalar yüklenemedi", "Couldn't load files"));
    } finally {
      setLoadingMore(false);
    }
  };

  // The newest page read again (a file just made): new files go on top, the
  // older pages already listed stay — and so does where "Daha fazla göster"
  // carries on from.
  const refresh = async () => {
    const page = await fetchGeneratedFilesPage(kind);
    setFiles((have) => mergeById(page.files, have));
    setNextBefore((at) => at || (page.hasMore ? page.nextBefore : null));
  };

  // A file still being made when the wait for it ended: the list is read
  // again a little later, while the panel is open, so it shows up here.
  const lateTimers = useRef([]);
  useEffect(() => {
    const timers = lateTimers;
    return () => timers.current.forEach(clearTimeout);
  }, []);
  const refreshLater = () => {
    lateTimers.current.forEach(clearTimeout);
    lateTimers.current = [60000, 150000].map((ms) => setTimeout(() => {
      refresh().catch(() => {});
    }, ms));
  };

  // One picked up again (not this panel's own request) finished: the list
  // is read again, so its file shows up here.
  const runningKey = running ? running.key : null;
  const lastRunning = useRef(null);
  const ownKeys = useRef(new Set());
  useEffect(() => {
    const was = lastRunning.current;
    lastRunning.current = runningKey;
    if (was && was !== runningKey && !ownKeys.current.has(was)) refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningKey]);

  // Kept in this tab while it is made (lib/generations.js): a reload picks
  // it up again under the same key instead of building — and billing — it
  // twice. The panel's thread is the mode's own (no Sohbet), as before.
  const handleGenerate = async () => {
    if (!rawData.trim() || busy) return;
    setGenerating(true);
    const entry = newGeneration({
      route, fields: { raw_data: rawData.trim(), title: title.trim() }, mode, making: cfg.making, origin: "panel",
    });
    ownKeys.current.add(entry.key);
    try {
      const result = await runGeneration(entry);
      // The generate response carries no id, so re-read the list — the new
      // file is stored server-side and comes back with its real, deletable id.
      try {
        await refresh();
      } catch {
        // (a replay without its bytes has no fileUrl: the list shows it once read)
        if (result.fileUrl) {
          setFiles((f) => [{ id: `tmp-${Date.now()}`, title: result.title, fileUrl: result.fileUrl, createdAt: new Date().toISOString() }, ...f]);
        }
      }
      setTitle("");
      setRawData("");
      // (found in this list after its deadline — lib/generations.js — it has
      // no reply of its own)
      toast.success(result.reply || t("Dosyan hazır! ✨", "Your file is ready! ✨"));
    } catch (e) {
      // (the terms still to accept: TermsGate shows instead — pages/Luna.jsx)
      if (isTermsRequired(e)) return;
      // Still being made when the wait for it ended (lib/api.js sends it
      // again under its key while the connection keeps going): it joins
      // this list once done — asking again would make (and bill) it twice.
      if (e?.stillWorking) {
        toast(t("Hâlâ hazırlanıyor; bitince bu listede görünecek. Yeniden istemene gerek yok 🌙",
          "Still being made — it'll show up in this list once done. No need to ask again 🌙"), { duration: 10000 });
        refreshLater();
        return;
      }
      // No answer at all for a minute (offline, a phone locked): kept, and
      // asked for again under its key once back (lib/generations.js).
      if (e?.connectionLost) {
        toast(t("Bağlantı koptu; geri gelince dosyanı kaldığı yerden alacağım. Yeniden istemene gerek yok 🌙",
          "The connection dropped — I'll pick your file up where it left off once you're back. No need to ask again 🌙"),
        { duration: 10000 });
        return;
      }
      // Past its deadline and not in this list: it may have failed — never
      // "still being made", and never sent again by itself: the form keeps
      // what was written, for a new request.
      if (e?.mayHaveFailed) {
        toast(t("Dosyan gelmedi; hazırlanırken bir sorun olmuş olabilir. İstersen yeniden oluştur.",
          "Your file didn't arrive — something may have gone wrong while making it. You can generate it again."),
        { duration: 10000 });
        return;
      }
      // (the server's own reason when it gives one: too long, a plan limit,
      // the file engine's "couldn't build it this time" (500), "couldn't
      // convert it" (502), "very busy" (503)...)
      toast.error(serverDetail(e, [...DETAIL_STATUSES, 500, 502, 503]) || t("Oluşturulamadı, tekrar dener misin?", "Couldn't generate that, try again?"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (file) => {
    setDeletingId(file.id);
    try {
      await deleteFile(file.id);
      setFiles((f) => f.filter((x) => x.id !== file.id));
    } catch {
      toast.error(t("Silinemedi", "Couldn't delete"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={onClose} data-testid={`doc-gen-panel-${kind}`}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-3xl border border-purple-400/20 p-6 max-h-[85vh] flex flex-col"
        style={{ backgroundColor: "#0c0818", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
        <div className="flex items-center justify-between mb-4 shrink-0">
          <h2 className="flex items-center gap-3 text-base font-bold text-white">
            <IconTile name={kind} size={40} /> {panelTitle}
          </h2>
          <button onClick={onClose} data-testid="doc-gen-close-button" aria-label={t("Kapat", "Close")}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {chatTip && (
          <p className="shrink-0 mb-3 rounded-xl border border-purple-400/15 bg-purple-400/[0.06] px-3.5 py-2.5 text-xs leading-relaxed text-purple-100/80"
            data-testid="doc-gen-chat-tip">
            {t("İpucu: sohbette istemen de yeter, dosya mesajın altına gelir. Örn: ",
              "Tip: you can also just ask in chat and the file shows up under the reply. E.g. ")}
            <span className="text-purple-50">{t(chatTip.tr, chatTip.en)}</span>
          </p>
        )}

        <div className="shrink-0 rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-4">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("Başlık (opsiyonel)", "Title (optional)")}
            data-testid="doc-gen-title-input"
            className="w-full bg-transparent outline-none text-base sm:text-sm text-white placeholder:text-white/30 border-b border-white/10 pb-2 mb-3"
          />
          <textarea
            value={rawData}
            onChange={(e) => setRawData(e.target.value)}
            placeholder={dataPlaceholder}
            rows={5}
            data-testid="doc-gen-data-input"
            className="w-full bg-transparent outline-none text-base sm:text-sm text-white placeholder:text-white/30 resize-none"
          />
          <button
            onClick={handleGenerate}
            disabled={!rawData.trim() || busy}
            data-testid="doc-gen-submit-button"
            className="w-full mt-3 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
            style={{ background: gradient }}
          >
            {busy ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Sparkles size={15} aria-hidden="true" />}
            {busy ? t("Oluşturuluyor...", "Generating...") : generateLabel}
          </button>
          {running && (
            // (a live region: a screen reader hears that it is being made)
            <p className="mt-2 flex items-baseline justify-center gap-2 text-[11px] text-purple-200/70" role="status"
              aria-live="polite" data-testid="doc-gen-progress">
              <span>{t("Oluşturuluyor… 1–4 dk sürebilir, sayfayı kapatma", "Creating… this can take 1–4 min, keep this page open")}</span>
              <span className="tabular-nums text-purple-200/50" aria-hidden="true" data-testid="doc-gen-elapsed">
                {elapsed(now - running.startedAt)}
              </span>
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex-1 space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-white/5 animate-pulse" />)}
          </div>
        ) : files.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-6">
            <IconTile name={kind} size={56} className="mb-1" />
            <p className="text-sm text-white/50 max-w-xs">
              {t("Henüz bir şey üretmedin. Yukarıya veri yapıştırıp başlayabilirsin.", "You haven't generated anything yet. Paste some data above to get started.")}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2">
            {files.map((f) => {
              const name = f.title || t("İsimsiz", "Untitled");
              return (
                <div key={f.id} data-testid="doc-gen-file-row"
                  className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <IconTile name={kind} size={28} />
                    <span className="text-sm text-white/85 truncate">{name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={f.fileUrl} download={downloadName((f.title || "luna-tablo").replace(/[^\p{L}\p{N}\s-]/gu, ""), dataUrlMime(f.fileUrl), fileExt)}
                      data-testid="doc-gen-download-button" aria-label={`${t("İndir", "Download")}: ${name}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5">
                      <Download size={15} aria-hidden="true" />
                    </a>
                    <button onClick={() => handleDelete(f)} disabled={deletingId === f.id}
                      data-testid="doc-gen-delete-button" aria-label={`${t("Sil", "Delete")}: ${name}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-red-300 hover:bg-red-400/10 disabled:opacity-50">
                      {deletingId === f.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} aria-hidden="true" />}
                    </button>
                  </div>
                </div>
              );
            })}
            {nextBefore && (
              <div className="flex justify-center pt-2 pb-1">
                <button onClick={loadMore} disabled={loadingMore} data-testid="doc-gen-load-more-button"
                  className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-purple-400/30 text-purple-200 hover:bg-purple-400/15 disabled:opacity-50">
                  {loadingMore && <Loader2 size={13} className="animate-spin" />}
                  {t("Daha fazla göster", "Show more")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
